import { useEffect, useState } from 'react';

import {
  Box,
  SearchInput,
  SearchInputField,
  VStack,
  HStack,
} from '@frontend/components';

import { isEmptyString } from 'utils/stringUtils';

import ConfigItemListPageListItem from './ConfigItemListPageListItem';
import type ConfigItemListPageListItemData from './types/ConfigItemListPageListItemData.interface';

export type ConfigItemListPageListProps = {
  items: ConfigItemListPageListItemData[];
  actions?: React.ReactNode;
  emptyState?:
    | React.ReactNode
    | ((params: {
        isFiltered: boolean;
        searchTerm: string;
      }) => React.ReactNode);
} & (
  | {
      isSearchable: true;
      searchPlaceholder: string;
    }
  | {
      isSearchable?: false | undefined;
      searchPlaceholder?: never;
    }
);

const ConfigItemListPageList = ({
  isSearchable,
  searchPlaceholder,
  items,
  actions,
  emptyState,
}: ConfigItemListPageListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);

  useEffect(() => {
    if (searchTerm && !isEmptyString(searchTerm)) {
      setFilteredItems(
        items.filter(
          ({ name }) =>
            name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1,
        ),
      );
    } else {
      setFilteredItems(items);
    }
  }, [items, searchTerm]);

  // Reset search term on new item.
  useEffect(() => {
    setSearchTerm('');
  }, [items.length]);

  return (
    <VStack alignItems="stretch" spacing={2}>
      {isSearchable && (
        <HStack spacing={2.5} justifyContent="space-between" w="100%">
          <SearchInput
            size="lg"
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm('')}
            value={searchTerm}
          >
            <SearchInputField placeholder={searchPlaceholder} />
          </SearchInput>
          {actions}
        </HStack>
      )}
      {filteredItems.length > 0 ? (
        <Box as="ul" boxShadow="page">
          {filteredItems.map((item) => {
            const { uid, ...itemProps } = item;

            return <ConfigItemListPageListItem key={uid} {...itemProps} />;
          })}
        </Box>
      ) : typeof emptyState === 'function' ? (
        emptyState({ isFiltered: !isEmptyString(searchTerm), searchTerm })
      ) : (
        emptyState
      )}
    </VStack>
  );
};

export default ConfigItemListPageList;
